import { useMemo } from 'react';
import { loadTransactions } from '@/lib/posStorage';
import { getCurrencySymbol } from '@/lib/bch';
import { usePOS } from '@/contexts/POSContext';

/**
 * Analyze transactions and return the top N most frequent subtotal amounts.
 * Only considers the last 200 transactions and rounds to 2 decimal places.
 * Ignores refunded transactions and 0-amount transactions.
 */
function getFrequentAmounts(limit = 4): number[] {
  const txs = loadTransactions();
  const recent = txs
    .filter((tx) => !tx.refunded && tx.subtotal > 0)
    .slice(0, 200);

  if (recent.length < 3) return []; // Need at least a few sales to detect patterns

  // Count frequency of each subtotal (rounded to 2 decimal places)
  const freq = new Map<number, number>();
  for (const tx of recent) {
    const key = parseFloat(tx.subtotal.toFixed(2));
    freq.set(key, (freq.get(key) ?? 0) + 1);
  }

  // Only show amounts that appeared at least 2 times
  const repeated = Array.from(freq.entries())
    .filter(([, count]) => count >= 2)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([amount]) => amount)
    .sort((a, b) => a - b); // Sort by amount ascending for display

  return repeated;
}

interface QuickAmountsProps {
  onSelect: (amount: number) => void;
}

export function QuickAmounts({ onSelect }: QuickAmountsProps) {
  const { settings, isPro } = usePOS();
  const amounts = useMemo(() => getFrequentAmounts(4), []);
  const sym = getCurrencySymbol(settings.currency);

  // Don't render if we don't have enough data or user isn't Pro
  if (!isPro || amounts.length === 0) return null;

  return (
    <div className="shrink-0">
      <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-1.5 px-0.5">
        Quick charge
      </p>
      <div className="flex gap-2">
        {amounts.map((amount) => (
          <button
            key={amount}
            onClick={() => onSelect(amount)}
            className="flex-1 py-2.5 rounded-xl bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-sm font-bold text-zinc-700 dark:text-zinc-200 hover:border-[#0AC18E]/50 hover:bg-[#0AC18E]/5 active:scale-95 transition-all"
          >
            {sym}{amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2)}
          </button>
        ))}
      </div>
    </div>
  );
}
